import { useState, type FormEvent } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import authApi from '@/services/auth';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import { PasswordStrengthMeter, validatePassword } from '@/components/auth';
import { validateRedirectUrl } from '@/utils/redirect';

/**
 * Reset Password Page
 * Allows users to set a new password using a token from the reset email
 *
 * WP-SEC-27: also the "set your password" step after email verification.
 * VerifyEmailPage navigates here with the passwordSetupToken it received and
 * `?setup=1`; the token and endpoint are the password-reset ones, only the
 * wording differs.
 */
export default function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const isSetup = searchParams.get('setup') === '1';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Track if validation has been attempted (for password requirements highlighting)
  const [passwordValidationAttempted, setPasswordValidationAttempted] = useState(false);

  // After "set your password" (WP-SEC-27), keep a pending post-sign-up
  // redirect (e.g. an invitation) that VerifyEmailPage handed over.
  const handleSignInNavigation = () => {
    const redirect = isSetup ? validateRedirectUrl(searchParams.get('redirect')) : null;
    if (redirect) {
      navigate(`/login?email_verified=true&redirect=${encodeURIComponent(redirect)}`);
    } else {
      navigate(isSetup ? '/login?email_verified=true' : '/login');
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords match first (doesn't trigger password requirements highlighting)
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Mark validation as attempted (for requirements highlighting)
    setPasswordValidationAttempted(true);

    // Validate password strength
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    setIsLoading(true);

    try {
      await authApi.resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      if (err instanceof Error) {
        // Provide user-friendly error messages
        if (err.message.includes('expired')) {
          setError('This password reset link has expired. Please request a new one.');
        } else if (err.message.includes('invalid')) {
          setError('This password reset link is invalid. Please request a new one.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
          <svg
            className="h-6 w-6 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Link</h2>
        <p className="text-gray-600 mb-6">
          This password reset link is invalid or has been used already.
        </p>
        <Link to="/forgot-password">
          <Button className="w-full">Request new reset link</Button>
        </Link>
      </div>
    );
  }

  if (success) {
    return (
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
          <svg
            className="h-6 w-6 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {isSetup ? 'Your account is ready' : 'Password Reset Successful'}
        </h2>
        <p className="text-gray-600 mb-6">
          {isSetup
            ? 'Your password is set. You can now sign in.'
            : 'Your password has been reset successfully. You can now sign in with your new password.'}
        </p>
        <Button className="w-full" onClick={handleSignInNavigation}>
          Sign in
        </Button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
        {isSetup ? 'Set your password' : 'Reset your password'}
      </h2>
      <p className="text-sm text-gray-600 text-center mb-6">
        {isSetup
          ? "Your email is verified. Choose the password you'll use to sign in."
          : 'Enter your new password below.'}
      </p>

      {/* Only show top Alert for server errors or "Passwords do not match" -
          password validation errors are shown inline below the password field */}
      {error && (!passwordValidationAttempted || error === 'Passwords do not match' ||
        error.includes('expired') || error.includes('invalid') || error.includes('Failed')) && (
        <Alert variant="error" className="mb-4" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Input
            label="New password"
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              // Clear validation state when user starts typing again
              if (passwordValidationAttempted) {
                setPasswordValidationAttempted(false);
                setError('');
              }
            }}
            placeholder="Enter your new password"
            autoComplete="new-password"
            autoFocus
            // Show inline error for password validation failures (not for "Passwords do not match")
            error={
              passwordValidationAttempted && error && error !== 'Passwords do not match'
                ? error
                : undefined
            }
            required
          />
          <div className="mt-2">
            <PasswordStrengthMeter
              password={password}
              showRequirements
              validationAttempted={passwordValidationAttempted}
            />
          </div>
        </div>

        <Input
          label="Confirm new password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm your new password"
          autoComplete="new-password"
          error={
            confirmPassword && password !== confirmPassword
              ? 'Passwords do not match'
              : undefined
          }
          required
        />

        <Button type="submit" className="w-full" isLoading={isLoading}>
          {isSetup ? 'Set password' : 'Reset password'}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-gray-600">
        Remember your password?{' '}
        <Link
          to="/login"
          className="font-medium text-primary-600 hover:text-primary-500"
        >
          Sign in
        </Link>
      </p>
    </div>
  );
}
